import type { RouteRecordRaw } from 'vue-router'

export type RouteModules = Record<string, { default: RouteRecordRaw[] }>

// ARCHITECTURE.md §2: adding a module touches no shared file — its routes.ts is picked up here.
const discovered = import.meta.glob<{ default: RouteRecordRaw[] }>('/src/modules/*/routes.ts', { eager: true })

/** `/src/modules/<id>/routes.ts` → `<id>`. */
export function moduleIdOf(file: string): string {
  const match = /\/modules\/([^/]+)\/[^/]+$/.exec(file)
  if (!match?.[1]) throw new Error(`Not a module file: ${file}`)
  return match[1]
}

function stamp(route: RouteRecordRaw, moduleId: string): RouteRecordRaw {
  const stamped = { ...route, meta: { ...route.meta, moduleId } } as RouteRecordRaw
  if (route.children) stamped.children = route.children.map((child) => stamp(child, moduleId))
  return stamped
}

function collectNames(route: RouteRecordRaw, into: string[]): void {
  if (typeof route.name === 'string') into.push(route.name)
  route.children?.forEach((child) => collectNames(child, into))
}

/**
 * Flattens every module's `routes.ts` (default export: `RouteRecordRaw[]`) into one list, stamps `meta.moduleId`
 * with the module folder (the i18n loader uses it), and refuses two modules declaring the same route name.
 */
export function collectRoutes(modules: RouteModules = discovered): RouteRecordRaw[] {
  const owners = new Map<string, string>()
  const routes: RouteRecordRaw[] = []
  for (const file of Object.keys(modules).sort()) {
    const moduleId = moduleIdOf(file)
    const declared = modules[file]?.default
    if (!Array.isArray(declared)) throw new Error(`${file} must export default an array of route records`)
    for (const route of declared) {
      const names: string[] = []
      collectNames(route, names)
      for (const name of names) {
        const owner = owners.get(name)
        if (owner) throw new Error(`Route name "${name}" is declared by both modules "${owner}" and "${moduleId}"`)
        owners.set(name, moduleId)
      }
      routes.push(stamp(route, moduleId))
    }
  }
  return routes
}
